// Hardcoded demo products for the demo flow
// These match the existing UI's expected field structure (+ variant fields)

import type { ProductVariant } from '../../redux/productSlice';

/** Bump when catalog contents change so existing demo DBs reseed. */
export const DEMO_CATALOG_VERSION = 5;

export const DEMO_CATALOG_VERSION_STORAGE_KEY = 'demo_catalog_version';

export interface DemoProductData {
  bp_owner_id: string;
  bp_product_location_id: string;
  bp_product_barcode: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_price_type: 'fixed' | 'per_mass';
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number | null;
  bp_product_selling_price: number | null;
  bp_product_quantity: number | null;
  bp_product_size: string;
  bp_minimum_stock_level: number;
  bp_product_model: string;
  bp_product_image: string;
  bp_product_deleted: boolean;
  bp_product_is_favourite: boolean;
  bp_product_created_at: number;
  bp_product_updated_at: number;
  bp_product_has_variants?: boolean;
  bp_product_variant_attributes?: string[];
  bp_product_variants?: ProductVariant[];
}

const now = Date.now();

const OWNER = 'demo_owner';
const LOCATION = 'demo_location';

function makeVariant(params: {
  id: string;
  sku: string;
  attributes: Record<string, string>;
  barcode: string;
  buying: number;
  selling: number;
  qty: number;
  displayName: string;
  image?: string;
  minStock?: number;
}): ProductVariant {
  return {
    _id: params.id,
    bp_variant_sku: params.sku,
    bp_variant_attributes: params.attributes,
    bp_variant_barcode: params.barcode,
    bp_variant_buying_price: params.buying,
    bp_variant_selling_price: params.selling,
    bp_variant_quantity: params.qty,
    bp_variant_image: params.image ?? '',
    bp_variant_minimum_stock_level: params.minStock ?? 5,
    bp_variant_display_name: params.displayName,
  };
}

export const DEMO_PRODUCTS: DemoProductData[] = [
  // 1. Johnnie Walker · 750ml
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '6001234567001',
    bp_product_name: 'Johnnie Walker',
    bp_product_category: 'Beverages',
    bp_product_sub_category: 'Spirits',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: 1800,
    bp_product_selling_price: 2500,
    bp_product_quantity: 24,
    bp_product_size: '750ml',
    bp_minimum_stock_level: 5,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1785974213/bizplus/demoproducts/hohnie_walker_g4pzmh.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: true,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: false,
    bp_product_variant_attributes: [],
    bp_product_variants: [],
  },
  // 2. Rice — sack sold per kg (not per 2kg item)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '5678901234567',
    bp_product_name: 'Rice',
    bp_product_category: 'Groceries',
    bp_product_sub_category: 'Staples',
    bp_product_price_type: 'per_mass',
    bp_product_unit_of_mass: 'kg',
    bp_product_mass: '1',
    bp_product_buying_price: 90,
    bp_product_selling_price: 130,
    bp_product_quantity: 50,
    bp_product_size: '',
    bp_minimum_stock_level: 10,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1785974306/bizplus/demoproducts/loose_rice_vof01f.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: true,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: false,
    bp_product_variant_attributes: [],
    bp_product_variants: [],
  },
  // 3. Paracetamol · 500mg / 1000mg (variant)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '',
    bp_product_name: 'Paracetamol',
    bp_product_category: 'Pharmacy',
    bp_product_sub_category: 'Pain Relief',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: null,
    bp_product_selling_price: null,
    bp_product_quantity: null,
    bp_product_size: '',
    bp_minimum_stock_level: 10,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1785974215/bizplus/demoproducts/paracetamol_rbm3sj.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: false,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: true,
    bp_product_variant_attributes: ['Strength'],
    bp_product_variants: [
      makeVariant({
        id: 'demo_var_paracetamol_500',
        sku: 'PARA-500',
        attributes: { Strength: '500mg' },
        barcode: '6001234567101',
        buying: 20,
        selling: 35,
        qty: 80,
        displayName: '500mg',
      }),
      makeVariant({
        id: 'demo_var_paracetamol_1000',
        sku: 'PARA-1000',
        attributes: { Strength: '1000mg' },
        barcode: '6001234567102',
        buying: 30,
        selling: 50,
        qty: 45,
        displayName: '1000mg',
      }),
    ],
  },
  // 4. Fresh Tomatoes (unchanged)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '3456789012345',
    bp_product_name: 'Fresh Tomatoes',
    bp_product_category: 'Groceries',
    bp_product_sub_category: 'Vegetables',
    bp_product_price_type: 'per_mass',
    bp_product_unit_of_mass: 'kg',
    bp_product_mass: '1',
    bp_product_buying_price: 80,
    bp_product_selling_price: 120,
    bp_product_quantity: 20,
    bp_product_size: '',
    bp_minimum_stock_level: 5,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1771785646/bizplus/demoproducts/122220_main_a9ubqp.avif',
    bp_product_deleted: false,
    bp_product_is_favourite: true,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: false,
    bp_product_variant_attributes: [],
    bp_product_variants: [],
  },
  // 5. FreePods · Black / White (variant) — replaces White Bread
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '',
    bp_product_name: 'FreePods',
    bp_product_category: 'Electronics',
    bp_product_sub_category: 'Audio',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: null,
    bp_product_selling_price: null,
    bp_product_quantity: null,
    bp_product_size: '',
    bp_minimum_stock_level: 5,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1785974212/bizplus/demoproducts/oraimo-FreePods-3C_ielaf9.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: true,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: true,
    bp_product_variant_attributes: ['Color'],
    bp_product_variants: [
      makeVariant({
        id: 'demo_var_freepods_black',
        sku: 'FPOD-BLK',
        attributes: { Color: 'Black' },
        barcode: '6001234567201',
        buying: 2500,
        selling: 3500,
        qty: 30,
        displayName: 'Black',
      }),
      makeVariant({
        id: 'demo_var_freepods_white',
        sku: 'FPOD-WHT',
        attributes: { Color: 'White' },
        barcode: '6001234567202',
        buying: 2500,
        selling: 3500,
        qty: 25,
        displayName: 'White',
      }),
    ],
  },
  // 6. Fresh Juice · 300ml / 500ml (variant, per item)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '',
    bp_product_name: 'Fresh Juice',
    bp_product_category: 'Beverages',
    bp_product_sub_category: 'Juice',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: null,
    bp_product_selling_price: null,
    bp_product_quantity: null,
    bp_product_size: '',
    bp_minimum_stock_level: 8,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1785974217/bizplus/demoproducts/fresh_juice_a6nqnb.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: false,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: true,
    bp_product_variant_attributes: ['Size'],
    bp_product_variants: [
      makeVariant({
        id: 'demo_var_juice_300',
        sku: 'JUICE-300',
        attributes: { Size: '300ml' },
        barcode: '6001234567301',
        buying: 40,
        selling: 70,
        qty: 40,
        displayName: '300ml',
      }),
      makeVariant({
        id: 'demo_var_juice_500',
        sku: 'JUICE-500',
        attributes: { Size: '500ml' },
        barcode: '6001234567302',
        buying: 55,
        selling: 100,
        qty: 35,
        displayName: '500ml',
      }),
    ],
  },
  // 7. Coca Cola (unchanged pricing/stock/image)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '1234567890123',
    bp_product_name: 'Coca Cola',
    bp_product_category: 'Beverages',
    bp_product_sub_category: 'Soft Drinks',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: 40,
    bp_product_selling_price: 60,
    bp_product_quantity: 50,
    bp_product_size: '500ml',
    bp_minimum_stock_level: 10,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1771786355/bizplus/demoproducts/images_1_zursmp.jpg',
    bp_product_deleted: false,
    bp_product_is_favourite: true,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: false,
    bp_product_variant_attributes: [],
    bp_product_variants: [],
  },
  // 8. Milk (kept)
  {
    bp_owner_id: OWNER,
    bp_product_location_id: LOCATION,
    bp_product_barcode: '4567890123456',
    bp_product_name: 'Milk',
    bp_product_category: 'Dairy',
    bp_product_sub_category: 'Milk',
    bp_product_price_type: 'fixed',
    bp_product_unit_of_mass: '',
    bp_product_mass: '',
    bp_product_buying_price: 100,
    bp_product_selling_price: 140,
    bp_product_quantity: 20,
    bp_product_size: '1L',
    bp_minimum_stock_level: 10,
    bp_product_model: '',
    bp_product_image:
      'https://res.cloudinary.com/dbi34vmol/image/upload/v1771785645/bizplus/demoproducts/43275_main_l91abj.avif',
    bp_product_deleted: false,
    bp_product_is_favourite: false,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_product_has_variants: false,
    bp_product_variant_attributes: [],
    bp_product_variants: [],
  },
];
